import groupBy from 'lodash.groupby';
import { addWeeks, setDay } from 'date-fns';

// Selectors
import { makeSelectOnlyMyTimesheetsCheckboxValue } from 'services/onlyMyTimesheetsCheckbox/selectors';
// @ts-ignore
import { selectSelectedTableCellRows } from 'services/selectedTableCells/selectors';
// @ts-ignore
import { makeSelectUsersMeValue } from 'services/usersMe/selectors';
// @ts-ignore
import { selectCurrentOrganizationId } from '../currentOrganizationId/selectors';
// @ts-ignore
import { selectCurrentSeasonId } from '../currentSeasonId/selectors';
// @ts-ignore
import { selectCurrentEntityId } from '../currentEntityId/selectors';

// Utils
// @ts-ignore
import createDeepEqualSelector from '../../utils/reselect/createDeepEqualSelector';

// HACK: disobeys folder structure rules
import {
    selectFirstDate,
    selectLastDate
    // @ts-ignore
} from '../../components/DatePagination/selectors';

// Workspaces
import {
    makeSelectQueryStringBooleanValue,
    makeSelectQueryStringValue
} from '../../workspaces/react-router-selectors';

// Types
import { Timesheet } from './types';

interface State {
    timesheets: Record<string, Timesheet>;
}

interface Props {
    timesheetId?: number;
}

const selectTimesheets = (state: State): Record<string, Timesheet> =>
    state.timesheets;
const selectTimesheetId = (state: State, props: Props): any =>
    props.timesheetId && `${props.timesheetId}`;

const selectTimesheetsArray = createDeepEqualSelector(
    selectTimesheets,
    (timesheetsState: any) => Object.values(timesheetsState)
);

const selectTimesheet = createDeepEqualSelector(
    [selectTimesheets, selectTimesheetId],
    (timesheetsState: any, timesheetIdProp: any) =>
        timesheetIdProp && timesheetsState[timesheetIdProp]
);

const makeSelectTimesheetValue = (field: keyof Timesheet): any =>
    createDeepEqualSelector(
        selectTimesheet,
        (timesheetState: any) => timesheetState && timesheetState[field]
    );

const selectTimeshetsGroupedByGroupId = createDeepEqualSelector(
    selectTimesheets,
    (timesheets: Record<string, Timesheet>): Record<number, Timesheet[]> =>
        groupBy(timesheets, 'groupId')
);

const makeSelectFilteredSelectedTimesheets = (): any =>
    createDeepEqualSelector(
        selectSelectedTableCellRows,
        selectTimesheets,
        (selectSelectedTableCellsState: any, selectTimesheetsState: any) =>
            selectSelectedTableCellsState &&
            selectSelectedTableCellsState.length &&
            selectTimesheetsState &&
            selectSelectedTableCellsState.filter((timesheet: number) => {
                if (!selectTimesheetsState[timesheet]) return false;

                const { isApproved, lastExportedAt } = selectTimesheetsState[
                    timesheet
                ];

                return isApproved && !lastExportedAt;
            })
    );

const selectLoadTimesheetsParams = createDeepEqualSelector(
    [
        selectCurrentOrganizationId,
        selectCurrentSeasonId,
        selectCurrentEntityId,
        selectFirstDate,
        selectLastDate,
        makeSelectQueryStringValue('page'),
        makeSelectQueryStringValue('count'),
        makeSelectQueryStringValue('sort'),
        makeSelectQueryStringValue('entities'),
        makeSelectQueryStringValue('workTypes'),
        makeSelectQueryStringValue('groups'),
        makeSelectOnlyMyTimesheetsCheckboxValue('checked'),
        makeSelectUsersMeValue('id'),
        makeSelectQueryStringBooleanValue('createTimesheetModal')
    ],
    (
        currentOrganizationIdState: any,
        currentSeasonIdState: any,
        currentEntityIdState: any,
        firstDateState: any,
        lastDateState: any,
        queryStringPageState: any = 1,
        queryStringCountState: any = 25,
        queryStringSortState: any = 'startDate_ascending',
        queryStringEntitiesState: any,
        queryStringWorkTypesState: any,
        queryStringGroupsState: any,
        onlyMyTimesheetsIsCheckedState: boolean,
        userIdState: any,
        createTimesheetModalIsOpen: boolean
    ) => {
        let params;

        /*
        Load timesheets for certain weeks (based on the week selector 5 weeks).
        These timesheets are used to validate if a group already has a timesheet for a specific date
        */
        if (createTimesheetModalIsOpen) {
            const mondayOfTheWeek = setDay(new Date(), 1);

            const startDate = addWeeks(mondayOfTheWeek, -1);

            const endDate = addWeeks(startDate, 5);

            params = {
                count: true,
                organizationId: currentOrganizationIdState,
                filter: {
                    where: {
                        startDate: {
                            $gte: startDate
                        },
                        endDate: {
                            $lte: endDate
                        }
                    }
                }
            };
        } else {
            params = {
                count: true,
                organizationId: currentOrganizationIdState,
                filter: {
                    limit: queryStringCountState,
                    offset: (queryStringPageState - 1) * queryStringCountState,
                    order: queryStringSortState
                        .split('_')
                        .map((value: any) => {
                            if (value === 'descending') return 'desc';
                            return value;
                        })
                        .join(' '),
                    require: {
                        $and: [
                            {
                                deletedAt: { $exists: false },
                                ...(queryStringWorkTypesState
                                    ? {
                                          workTypeId: {
                                              $in: queryStringWorkTypesState
                                          }
                                      }
                                    : {}),
                                ...(queryStringGroupsState
                                    ? {
                                          groupId: {
                                              $in: queryStringGroupsState
                                          }
                                      }
                                    : {}),
                                ...(currentSeasonIdState
                                    ? {
                                          'entity.seasonId': currentSeasonIdState
                                      }
                                    : {}),
                                ...(onlyMyTimesheetsIsCheckedState
                                    ? { createdByUserId: userIdState }
                                    : {}),
                                $and: [
                                    ...(currentEntityIdState
                                        ? [
                                              {
                                                  $or: [
                                                      {
                                                          'entity.path': {
                                                              $or: [
                                                                  {
                                                                      $like: `/${currentEntityIdState}/%`
                                                                  },
                                                                  {
                                                                      $equals: `/${currentEntityIdState}`
                                                                  }
                                                              ]
                                                          }
                                                      },
                                                      {
                                                          entityId: currentEntityIdState
                                                      }
                                                  ]
                                              }
                                          ]
                                        : []),
                                    ...(queryStringEntitiesState &&
                                    queryStringEntitiesState.length
                                        ? [
                                              {
                                                  $or: queryStringEntitiesState.map(
                                                      (value: any) => ({
                                                          $or: [
                                                              {
                                                                  'entity.path': {
                                                                      $or: [
                                                                          {
                                                                              $like: `%/${value}/%`
                                                                          },
                                                                          {
                                                                              $like: `%/${value}`
                                                                          }
                                                                      ]
                                                                  }
                                                              },
                                                              {
                                                                  entityId: value
                                                              }
                                                          ]
                                                      })
                                                  )
                                              }
                                          ]
                                        : [])
                                ]
                            },
                            {
                                $or: [
                                    {
                                        startDate: {
                                            $lte: lastDateState
                                        },
                                        endDate: {
                                            $gte: firstDateState
                                        }
                                    },
                                    {
                                        startDate: {
                                            $lte: lastDateState
                                        },
                                        endDate: {
                                            $gte: firstDateState
                                        }
                                    }
                                ]
                            }
                        ]
                    }
                }
            };
        }

        return params;
    }
);

export {
    selectTimesheets,
    selectTimesheetsArray,
    selectTimesheet,
    makeSelectTimesheetValue,
    selectLoadTimesheetsParams,
    makeSelectFilteredSelectedTimesheets,
    selectTimeshetsGroupedByGroupId
};
